import type { BoxProps } from '@chakra-ui/react';
import { Box, Flex, Icon, Text } from '@chakra-ui/react';
import type { AvatarProps } from '@halo/ui-chat-react';
import { Avatar, GroupIcon, RippleEffect } from '@halo/ui-chat-react';
import { Checkmark } from '@halo/ui-react';

export interface ItemSelectorProps extends Omit<BoxProps, 'onClick'> {
  id: string;
  name: React.ReactNode;
  username: string;
  avatar: string;
  avatarProps?: AvatarProps;
  error?: boolean;
  onClick?: (id: string) => void;
  rippleEffect?: boolean;
  hoverable?: boolean;
  isChecked: boolean;
  isDisabled?: boolean;
  isDelete?: boolean;
  isGroup?: boolean;
}

export const ItemSelector = memo(function ItemSelector({
  id,
  name,
  username,
  avatar,
  avatarProps,
  error,
  onClick,
  rippleEffect = true,
  hoverable = true,
  isChecked,
  isDisabled = false,
  isDelete = false,
  isGroup = false,
  ...props
}: ItemSelectorProps) {
  return (
    <RippleEffect w="full" enabled={rippleEffect}>
      <Box
        className="ItemSelector"
        px="2"
        pt="2"
        w="full"
        overflow="hidden"
        _hover={
          hoverable
            ? {
                bg: 'bg.subtle',
              }
            : undefined
        }
      >
        <Flex gap="2.5" align="center">
          <Box mb="2">
            <Checkmark
              filled
              size="sm"
              borderRadius="sm"
              cursor={isDisabled ? 'not-allowed' : 'pointer'}
              disabled={isDisabled}
              onClick={() => onClick?.(id)}
              checked={isChecked}
            />
          </Box>
          <Avatar
            size="sm"
            showUserError={error}
            src={avatar}
            activeShake={false}
            mb="2"
            {...avatarProps}
          />
          <Box
            flex="1"
            pb={isDelete ? '0' : '2'}
            borderBottom={isDelete ? 'none' : '1px solid'}
            borderColor="border.subtle"
            overflow="hidden"
            onClick={() => onClick?.(id)}
            cursor={isDisabled ? 'not-allowed' : 'pointer'}
            {...props}
          >
            <Flex align="center" justify="space-between" gap="2.5">
              <Box overflow="hidden">
                <Flex align="center" gap="1">
                  {isGroup && (
                    <Icon>
                      <GroupIcon />
                    </Icon>
                  )}
                  <Box
                    color="fg"
                    fontSize="sm"
                    fontWeight="semibold"
                    lineHeight="normal"
                    truncate
                  >
                    {name}
                  </Box>
                </Flex>
                <Flex>
                  <Text
                    fontSize="xs"
                    fontWeight="medium"
                    lineHeight="normal"
                    color="fg.muted"
                    truncate
                  >
                    {!isDelete && username && <>@{username}</>}
                  </Text>
                </Flex>
              </Box>
            </Flex>
          </Box>
        </Flex>
      </Box>
    </RippleEffect>
  );
});
